import { useState } from "react";
import { useAuthContext } from "@/hooks/useAuthContext";
import {
  useGetOfficeMembersQuery,
  useSendInvitationMutation,
  useCancelInvitationMutation,
  useGetInvitationsQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useTransferOwnershipMutation,
} from "@/redux/api/offices.rtkq";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CLEAR_OWNER_VALUE = "__clear_owner__";

const OfficeMembersPage = () => {
  const { getAccountId, isAccountAdmin, userProfile, refreshUser } =
    useAuthContext();
  const officeId = getAccountId() || "";
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("office_user");
  const [transferTargetId, setTransferTargetId] = useState("");
  const [isTransferConfirmOpen, setIsTransferConfirmOpen] = useState(false);
  const [memberToRemoveId, setMemberToRemoveId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: membersData, isLoading } = useGetOfficeMembersQuery(officeId, {
    skip: !officeId,
  });
  const { data: invitations } = useGetInvitationsQuery(officeId, {
    skip: !officeId || !isAccountAdmin(),
  });
  const [sendInvitation] = useSendInvitationMutation();
  const [cancelInvitation] = useCancelInvitationMutation();
  const [updateRole] = useUpdateMemberRoleMutation();
  const [removeMember, { isLoading: isRemoving }] = useRemoveMemberMutation();
  const [transferOwnership, { isLoading: isTransferring }] =
    useTransferOwnershipMutation();

  const currentOfficeRole = userProfile?.officeRoles?.find(
    (r) => r.officeId === officeId,
  );
  const isOwner =
    currentOfficeRole?.isOwner === true && !!membersData?.ownerUserId;
  const transferCandidates =
    membersData?.members.filter((m) => !m.isOwner) ?? [];
  const clearingOwnership = transferTargetId === CLEAR_OWNER_VALUE;
  const transferTargetMember = membersData?.members.find(
    (m) => m.userId === transferTargetId,
  );
  const transferTargetLabel = clearingOwnership
    ? "ללא בעלים"
    : transferTargetMember?.displayName ||
      transferTargetMember?.email ||
      "המשתמש";

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await sendInvitation({
        officeId,
        email: inviteEmail.trim(),
        role: inviteRole,
      }).unwrap();
      setInviteEmail("");
    } catch (err: any) {
      setErrorMessage(err.data?.message || "שגיאה בשליחת ההזמנה");
    }
  };

  const submitTransferOwnership = async () => {
    if (!transferTargetId) return;
    try {
      await transferOwnership({
        officeId,
        newOwnerUserId: clearingOwnership ? null : transferTargetId,
      }).unwrap();
      setTransferTargetId("");
      setIsTransferConfirmOpen(false);
      await refreshUser();
    } catch (err: any) {
      setIsTransferConfirmOpen(false);
      setErrorMessage(err.data?.message || "שגיאה בהעברת בעלות");
    }
  };

  const memberToRemove = membersData?.members.find(
    (m) => m.userId === memberToRemoveId,
  );
  const memberToRemoveLabel =
    memberToRemove?.displayName || memberToRemove?.email || "המשתמש";

  const submitRemoveMember = async () => {
    if (!memberToRemoveId) return;
    try {
      await removeMember({ officeId, userId: memberToRemoveId }).unwrap();
      setMemberToRemoveId(null);
    } catch (err: any) {
      setMemberToRemoveId(null);
      setErrorMessage(err.data?.message || "שגיאה בהסרת חבר");
    }
  };

  if (isLoading)
    return <div className="p-6 text-muted-foreground">טוען...</div>;

  return (
    <div className="container mx-auto max-w-5xl p-4 md:p-6" dir="rtl">
      <h1 className="mb-6 text-2xl font-bold text-foreground">חברי משרד</h1>

      {isAccountAdmin() && (
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-lg font-semibold">הזמנת משתמש</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              placeholder="כתובת אימייל"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            >
              <option value="office_user">משתמש</option>
              <option value="office_admin">מנהל</option>
            </select>
            <button
              onClick={handleInvite}
              className="rounded-lg bg-chen-900 px-4 py-2 text-white hover:opacity-90"
            >
              שלח הזמנה
            </button>
          </div>
        </div>
      )}

      {isOwner && (
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-lg font-semibold">העבר בעלות</h2>
          <p className="mb-3 text-sm text-muted-foreground">
            בעלות היא תג מעל תפקיד מנהל. אפשר גם להשאיר את המשרד ללא בעלים.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={transferTargetId}
              onChange={(e) => setTransferTargetId(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            >
              <option value="">בחר חבר משרד</option>
              <option value={CLEAR_OWNER_VALUE}>ללא בעלים</option>
              {transferCandidates.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {(m.displayName || m.email) +
                    (m.role === "office_admin" ? " (מנהל)" : " (משתמש)")}
                </option>
              ))}
            </select>
            <button
              onClick={() => setIsTransferConfirmOpen(true)}
              disabled={!transferTargetId || isTransferring}
              className="rounded-lg bg-chen-900 px-4 py-2 text-white hover:opacity-90 disabled:opacity-50"
            >
              {isTransferring
                ? "מעביר..."
                : clearingOwnership
                  ? "הסר בעלות"
                  : "העבר בעלות"}
            </button>
          </div>
        </div>
      )}

      <AlertDialog
        open={isTransferConfirmOpen}
        onOpenChange={setIsTransferConfirmOpen}
      >
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {clearingOwnership ? "הסרת בעלות" : "העברת בעלות"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {clearingOwnership
                ? "להשאיר את המשרד ללא בעלים? המנהלים יישארו עם הרשאותיהם."
                : `להעביר בעלות על המשרד אל ${transferTargetLabel}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void submitTransferOwnership();
              }}
              disabled={isTransferring || !transferTargetId}
            >
              {isTransferring
                ? "מעביר..."
                : clearingOwnership
                  ? "הסר בעלות"
                  : "העבר בעלות"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={errorMessage !== null}
        onOpenChange={(open) => {
          if (!open) setErrorMessage(null);
        }}
      >
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>שגיאה</AlertDialogTitle>
            <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogAction>אישור</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={memberToRemoveId !== null}
        onOpenChange={(open) => {
          if (!open) setMemberToRemoveId(null);
        }}
      >
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>הסרת חבר</AlertDialogTitle>
            <AlertDialogDescription>
              להסיר את {memberToRemoveLabel} מהמשרד?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void submitRemoveMember();
              }}
              disabled={isRemoving || !memberToRemoveId}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isRemoving ? "מסיר..." : "הסר"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {invitations && invitations.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold">הזמנות ממתינות</h2>
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="mb-2 flex flex-col gap-2 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <span className="text-foreground">{inv.email}</span>
                <span className="me-2 text-sm text-muted-foreground">
                  ({inv.role === "office_admin" ? "מנהל" : "משתמש"})
                </span>
              </div>
              <button
                onClick={() =>
                  cancelInvitation({ officeId, invitationId: inv.id })
                }
                className="self-start text-sm text-red-500 hover:text-red-700"
              >
                ביטול
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <h2 className="mb-3 text-lg font-semibold">
          חברים ({membersData?.members.length || 0})
        </h2>
        {membersData?.members.map((member) => (
          <div
            key={member.userId}
            className="mb-2 flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="font-medium text-foreground">
                {member.displayName || member.email}
                {member.isOwner && (
                  <span className="me-2 rounded-full bg-chen-900 px-2 py-0.5 text-xs text-white">
                    בעלים
                  </span>
                )}
              </div>
              <div className="truncate text-sm text-muted-foreground">
                {member.email}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isOwner && !member.isOwner ? (
                <select
                  value={member.role}
                  onChange={(e) =>
                    updateRole({
                      officeId,
                      userId: member.userId,
                      role: e.target.value,
                    })
                  }
                  className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground"
                >
                  <option value="office_user">משתמש</option>
                  <option value="office_admin">מנהל</option>
                </select>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {member.role === "office_admin" ? "מנהל" : "משתמש"}
                </span>
              )}
              {isAccountAdmin() && (
                <button
                  onClick={() => setMemberToRemoveId(member.userId)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  הסר
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OfficeMembersPage;
